import * as React from 'react';
import { useDebounce } from './use-debounce';

export interface UseRoundOnDebounceOptions {
  /** Current raw input value (controlled by the caller). */
  rawValue: string;
  /** Allowed decimal places, or `null` to skip normalisation entirely. */
  decimalPlaces: number | null;
  /**
   * Pure formatter — parses + rounds + pads the raw input to the canonical
   * representation, or returns `null` when the input isn't parseable.
   */
  format: (raw: string) => string | null;
  /** Called with the rounded value when the debounce settles. */
  onChange: (value: string) => void;
  /** Gate the hook (e.g. only round while focused). */
  enabled: boolean;
  /** @default 500 */
  delayMs?: number;
}

/**
 * After the user pauses for `delayMs`, emit a rounded value via `onChange` if
 * the typed fraction exceeds `decimalPlaces`. Shorter fractions stay raw so
 * mid-typing isn't disrupted by premature padding. `format`/`onChange` are
 * read from refs so unrelated parent re-renders don't trigger a spurious emit.
 */
export function useRoundOnDebounce({
  rawValue,
  decimalPlaces,
  format,
  onChange,
  enabled,
  delayMs = 500
}: UseRoundOnDebounceOptions): void {
  const debouncedRawValue = useDebounce(rawValue, delayMs);

  const formatRef = React.useRef(format);
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    formatRef.current = format;
    onChangeRef.current = onChange;
  });

  React.useEffect(() => {
    if (!enabled || decimalPlaces == null || debouncedRawValue === '') return;
    const dotIdx = debouncedRawValue.indexOf('.');
    const fractionLen = dotIdx === -1 ? 0 : debouncedRawValue.length - dotIdx - 1;
    if (fractionLen <= decimalPlaces) return;
    const formatted = formatRef.current(debouncedRawValue);
    if (formatted != null && formatted !== debouncedRawValue) onChangeRef.current(formatted);
  }, [debouncedRawValue, enabled, decimalPlaces]);
}
